<script>
import CustomersApi from "@/services/customers-api";
import ReportingApi from "@/services/reporting-api";

const today = new Date().toLocaleDateString("en-CA");

export default {
  props: {
    firstDay: { type: String, default: null },
    lastDay: { type: String, default: null }
  },

  data() {
    return {
      start: this.firstDay,
      end: today,
      selectedCustomer: null,
      customersList: [],
      loading: false,
      viewMode: "total",
      dailyTotals: [],
      detailsList: [],
      chartKey: 0,
      today,
      pagemodel: {
        currentPage: 1,
        perPage: 10,
        totalRows: 0,
        pageSize: 10
      },
      fields: [
        { key: "downloadedAt", label: "Date", thClass: "text-dark font-weight-bold" , tdClass: "text-dark font-weight-bold"},
        { key: "customerName", label: "Client", thClass: "text-dark font-weight-bold" },
        { key: "blNumber", label: "N° BL", thClass: "text-dark font-weight-bold", tdClass: "text-dark font-weight-bold"},
        { key: "downloadedByUsername", label: "Téléchargée par", thClass: "text-dark font-weight-bold" }
      ]
    };
  },

  computed: {
    customerId() {
      return this.selectedCustomer ? this.selectedCustomer.id : null;
    },

    isTotalMode() {
      return this.viewMode === "total";
    },

    isDetailsMode() {
      return this.viewMode === "details";
    },

    hasNoData() {
      return this.isTotalMode ? !this.dailyTotals.length : !this.detailsList.length;
    },

    emptyMessage() {
      return this.isTotalMode
        ? "Aucun téléchargement sur cette période."
        : "Aucun détail de téléchargement sur cette période.";
    },

    dailyChartSeries() {
      return [
        {
          name: "téléchargements",
          data: this.dailyTotals.map(item => item.downloadsCount)
        }
      ];
    },

    dailyChartOptions() {
      return {
        chart: {
          toolbar: {
            show: true
          }
        },
        stroke: {
          curve: "smooth",
          width: 3
        },
        colors: ["#50a5f1"],
        dataLabels: {
          enabled: false
        },
        xaxis: {
          categories: this.dailyTotals.map(item => item.date)
        },
        yaxis: {
          min: 0,
          forceNiceScale: true,
          labels: {
            formatter: value => Math.round(value)
          }
        },
        tooltip: {
          y: {
            formatter: value => Math.round(value)
          }
        }
      };
    }
  },

  methods: {
    compareDates(first, second) {
      const toDate = value => new Date(value.split("/").reverse().join("-"));
      return toDate(first) - toDate(second);
    },

    getCustomers() {
      CustomersApi.getCustomers(null, null).then(response => {
        this.customersList = response || [];
      });
    },

    setViewMode(mode) {
      if (this.viewMode === mode) {
        return;
      }

      this.viewMode = mode;
      this.resetPagination();
      this.loadDownloads();
    },

    resetPagination() {
      this.pagemodel.currentPage = 1;
      this.pagemodel.totalRows = 0;
    },

    onFiltersChanged() {
      this.resetPagination();
      this.loadDownloads();
    },

    onPerPageChanged() {
      this.resetPagination();
      this.loadDownloads();
    },

    loadDownloads() {
      if ((this.start && !this.end) || (!this.start && this.end)) {
        return;
      }

      if (this.isTotalMode) {
        this.loadDailyTotals();
        return;
      }

      this.loadDetails();
    },

    loadDailyTotals() {
      this.loading = true;

      ReportingApi.getCustomerDeliveryNoteDownloadDaily(
        this.start,
        this.end,
        this.customerId
      )
        .then(response => {
          this.dailyTotals = (response || []).sort((a, b) => this.compareDates(a.date, b.date));
          this.chartKey += 1;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    loadDetails() {
      this.loading = true;

      ReportingApi.getCustomerDeliveryNoteDownload(
        this.pagemodel.currentPage,
        this.pagemodel.perPage,
        this.start,
        this.end,
        this.customerId
      )
        .then(response => {
          if (response) {
            this.detailsList = response.list || [];
            this.pagemodel.totalRows = response.total || 0;
            this.pagemodel.currentPage = response.page || 1;
            this.pagemodel.perPage = response.pageSize || this.pagemodel.perPage;
            this.pagemodel.pageSize = response.pageSize || this.pagemodel.pageSize;
          } else {
            this.detailsList = [];
            this.pagemodel.totalRows = 0;
          }
        })
        .finally(() => {
          this.loading = false;
        });
    }
  },

  mounted() {
    this.getCustomers();
    this.loadDownloads();
  },

  watch: {
    start() {
      this.onFiltersChanged();
    },

    end() {
      this.onFiltersChanged();
    },

    selectedCustomer() {
      this.onFiltersChanged();
    }
  }
};
</script>

<template>
  <div class="clearfix m-4">

  <div class="d-flex align-items-center justify-content-between mb-4">
    <h4 class="card-title font-size-18 font-weight-bold text-dark mb-0">
      Historiques des téléchargements des BLs par API
    </h4>

    <div class="view-mode-selector">
      <button
        type="button"
        class="view-mode-option"
        :class="{ active: isTotalMode }"
        @click="start = firstDay ; setViewMode('total') "
      >
        <i class="mdi mdi-chart-line mr-1"></i>
        Total
      </button>

      <button
        type="button"
        class="view-mode-option"
        :class="{ active: isDetailsMode }"
        @click="setViewMode('details')"
      >
        <i class="mdi mdi-format-list-bulleted mr-1"></i>
        Détails
      </button>
    </div>
  </div>


    <div class="row mt-4 align-items-end">
      <div class="col-xl-3 col-lg-4 col-md-12 col-sm-12 col-xs-12 pr-lg-2">
        <b-form-group :label="$t('lists.customers.filter')" label-for="">
          <v-select
            class="style-vselect-big"
            style="font-size:1.2em;"
            :options="customersList"
            :placeholder="$t('lists.customers.label')"
            label="fullCompanyName"
            v-model="selectedCustomer"
          >
            <span slot="no-options">{{$t('lists.emptyLists.label')}}</span>
          </v-select>
        </b-form-group>
      </div>

      <div class="col-xl-3 col-lg-4 col-md-12 col-sm-12 col-xs-12 pr-lg-2">
        <b-form-group :label="$t('datPicker.startDate.label-no-date-selected')" label-for="">
          <b-form-datepicker
            v-model="start"
            :locale="$t('datPicker.locale')"
            :label-no-date-selected="$t('datPicker.startDate.label-no-date-selected')"
            dark
            close-button
            :label-reset-button="$t('datPicker.label-reset-button')"
            :label-close-button="$t('datPicker.label-close-button')"
            dropright
            hide-header
            :max="end || today"
          ></b-form-datepicker>
        </b-form-group>
      </div>

      <div class="col-xl-3 col-lg-4 col-md-12 col-sm-12 col-xs-12 pr-lg-2">
        <b-form-group :label="$t('datPicker.endDate.label-no-date-selected')" label-for="">
          <b-form-datepicker
            v-model="end"
            :locale="$t('datPicker.locale')"
            :label-no-date-selected="$t('datPicker.endDate.label-no-date-selected')"
            dark
            close-button
            :label-reset-button="$t('datPicker.label-reset-button')"
            :label-close-button="$t('datPicker.label-close-button')"
            dropright
            hide-header
            :min="start"
            :max="today"
          ></b-form-datepicker>
        </b-form-group>
      </div>


    </div>

    <div v-if="loading" class="text-center py-4">
      <b-spinner variant="primary" />
    </div>

    <div
      v-else-if="hasNoData"
      class="text-muted d-flex align-items-center justify-content-center"
      style="min-height: 20em;"
    >
      {{ emptyMessage }}
    </div>

    <div v-else-if="isTotalMode" class="mt-4">
      <apexchart
        :key="chartKey"
        type="line"
        height="320"
        :options="dailyChartOptions"
        :series="dailyChartSeries"
      />
    </div>

    <div v-else class="mt-4">
      <div class="table-responsive mb-0" style="max-height:27em;min-height:27em">
        <b-table
          :busy="loading"
          :striped="true"
          :small="true"
          :hover="true"
          :items="detailsList"
          :fields="fields"
          :per-page="pagemodel.perPage"
        >
          <template v-slot:table-busy>
            <div class="text-center my-2">
              <b-spinner class="m-2" variant="primary" role="status"></b-spinner>
            </div>
          </template>

          <template v-slot:cell(customerCode)="{ value }">
            <span :class="!value ? 'text-muted' : 'text-dark'">{{ !value ? '_' : value }}</span>
          </template>

          <template v-slot:cell(customer)="{ value }">
            <span :class="!value ? 'text-muted' : 'text-dark'">{{ !value ? '_' : value }}</span>
          </template>
        </b-table>
      </div>

      <div class="row pt-3 pl-2 mb-sm-4">
        <div class="col-sm-8">
          <div class="dataTables_paginate paging_simple_numbers float-left d-none d-md-flex">
            <b-form-select
              @input="onPerPageChanged()"
              v-model="pagemodel.perPage"
              size="sm"
              style="width: 5em"
              :options="[10,15,50,100]"
            ></b-form-select>
            <div class="d-none d-lg-inline pt-1">
              <span class="text-muted mb-0 ml-1">{{ $t('pagination.from') }} </span>
              <span class="font-weight-bold">{{ pagemodel.totalRows }} {{ $t('pagination.lines') }}</span>
            </div>
          </div>
        </div>

        <div class="col-sm-4">
          <div class="dataTables_paginate paging_simple_numbers float-right">
            <ul class="pagination pagination-rounded mb-0">
              <b-pagination
                v-model="pagemodel.currentPage"
                :per-page="pagemodel.pageSize"
                :total-rows="pagemodel.totalRows"
                @input="loadDetails()"
                :disabled="loading"
              ></b-pagination>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>


<style>
.view-mode-selector {
  display: inline-flex;
  padding: 3px;
  background: #f1f3f7;
  border: 1px solid #e5e8ef;
  border-radius: 8px;
}

.view-mode-option {
  padding: 5px 10px;
  color: #74788d;
  background: transparent;
  border: 0;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.2s ease;
  outline: none !important;
}

.view-mode-option i {
  font-size: 14px;
}

.view-mode-option:hover:not(.active) {
  color: var(--primary);
}

.view-mode-option.active {
  color: var(--primary);
  background: #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}
</style>